import axios from 'axios'

export const TOKEN_KEY = 'aannemerplus_token'
export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'
export const WEBSITE_BASE_URL = import.meta.env.VITE_WEBSITE_URL ?? 'http://localhost:5173'

// Create API client instance with base URL
const httpClient = axios.create({
  baseURL: API_BASE_URL
})

// Request interceptor: attach JWT token
httpClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor: unwrap { success, data } envelope + handle 401
httpClient.interceptors.response.use(
  (response) => {
    const body = response.data
    if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
      response.data = body.data
    }
    return response
  },
  (error) => {
    if (
      error.response?.status === 401 &&
      !error.config.url?.includes('/api/auth/login') &&
      !error.config.url?.includes('/api/auth/register')
    ) {
      localStorage.removeItem(TOKEN_KEY)
      window.location.href = (import.meta.env.BASE_URL || '/') + 'login'
    }
    return Promise.reject(error)
  }
)

export { httpClient }

// Manual API client with correct endpoints based on OpenAPI documentation
export const apiClient = {
  // Auth
  login: (data: { email: string; password: string }) => httpClient.post('/api/auth/login', data),
  register: (data: { email: string; password: string; firstName: string; lastName: string }) => httpClient.post('/api/auth/register', data),
  getMe: () => httpClient.get('/api/auth/me'),
  changePassword: (data: { currentPassword: string; newPassword: string }) => httpClient.put('/api/auth/password', data),
  updateProfile: (data: { firstName: string; lastName: string }) => httpClient.put('/api/auth/profile', data),
  forgotPassword: (data: { email: string }) => httpClient.post('/api/auth/forgot-password', data),
  resetPassword: (data: { email: string; token: string; newPassword: string }) => httpClient.post('/api/auth/reset-password', data),
  // Passwordless login for owners who claimed their site via email during onboarding
  requestLoginCode: (data: { email: string }) => httpClient.post('/api/auth/login-code/request', data),
  verifyLoginCode: (data: { email: string; code: string }) => httpClient.post('/api/auth/login-code/verify', data),

  // Projects
  getProjects: () => httpClient.get('/api/projects'),
  getProjectById: (id: string) => httpClient.get(`/api/projects/${id}`),
  getProjectBySlug: (slug: string) => httpClient.get(`/api/projects/slug/${slug}`),
  getWebsiteConfig: (slug: string) => httpClient.get(`/api/projects/${slug}/website`),
  createProject: (data: any) => httpClient.post('/api/projects', data),
  deleteProject: (id: string) => httpClient.delete(`/api/projects/${id}`),

  // Pages
  getPageById: (id: string) => httpClient.get(`/api/pages/${id}`),
  createPage: (projectId: string, data: any) => httpClient.post(`/api/pages/project/${projectId}`, data),
  updatePage: (id: string, data: any) => httpClient.put(`/api/pages/${id}`, data),
  deletePage: (id: string) => httpClient.delete(`/api/pages/${id}`),
  publishPageVersion: (id: string, versionId: string) => httpClient.post(`/api/pages/${id}/versions/${versionId}/publish`, {}),

  // Menus
  getMenuItems: (projectId: string, language: string) =>
    httpClient.get(`/api/projects/${projectId}/menus/${language}`),
  createMenuItem: (projectId: string, language: string, data: any) =>
    httpClient.post(`/api/projects/${projectId}/menus/${language}/items`, data),
  updateMenuItem: (projectId: string, language: string, index: number, data: any, parentIndex?: number) =>
    httpClient.put(`/api/projects/${projectId}/menus/${language}/items/${index}${parentIndex !== undefined ? `?parentIndex=${parentIndex}` : ''}`, data),
  deleteMenuItem: (projectId: string, language: string, index: number) =>
    httpClient.delete(`/api/projects/${projectId}/menus/${language}/items/${index}`),
  moveMenuItem: (projectId: string, language: string, index: number, data: any) =>
    httpClient.put(`/api/projects/${projectId}/menus/${language}/items/${index}/move`, data),
  publishMenu: (projectId: string, language: string) =>
    httpClient.post(`/api/projects/${projectId}/menus/${language}/publish`),
  discardMenuDraft: (projectId: string, language: string) =>
    httpClient.post(`/api/projects/${projectId}/menus/${language}/discard`),

  // Page Components
  getPageComponents: (pageId: string) => httpClient.get(`/api/pages/${pageId}/components`),
  createPageComponent: (pageId: string, data: any) => httpClient.post(`/api/pages/${pageId}/components`, data),
  reorderPageComponents: (pageId: string, data: { parentInstanceId: number | null; orderedIds: number[] }) =>
    httpClient.put(`/api/pages/${pageId}/components/reorder`, data),

  // Component Instances
  getComponentInstance: (instanceId: string) => httpClient.get(`/api/components/instances/${instanceId}`),
  updateComponentInstance: (instanceId: string, data: any) => httpClient.put(`/api/components/instances/${instanceId}`, data),
  deleteComponentInstance: (instanceId: string) => httpClient.delete(`/api/components/instances/${instanceId}`),
  duplicateComponentInstance: (instanceId: string) => httpClient.post(`/api/components/instances/${instanceId}/duplicate`, {}),

  // Components
  getProjectComponents: (projectId: string) => httpClient.get(`/api/components/project/${projectId}`),
  getComponent: (id: string) => httpClient.get(`/api/components/${id}`),
  createComponent: (projectId: string, data: any) => httpClient.post(`/api/components/project/${projectId}`, data),
  updateComponent: (id: string, data: any) => httpClient.put(`/api/components/${id}`, data),
  deleteComponent: (id: string) => httpClient.delete(`/api/components/${id}`),

  // Media
  getProjectMedia: (projectId: string) => httpClient.get(`/api/media/project/${projectId}`),
  uploadMedia: (projectId: string, data: any) => httpClient.post(`/api/media/project/${projectId}/upload`, data),
  getMedia: (id: string) => httpClient.get(`/api/media/${id}`),
  updateMedia: (id: string, data: any) => httpClient.put(`/api/media/${id}`, data),
  getMediaStats: (projectId: string) => httpClient.get(`/api/media/project/${projectId}/stats`),

  // Domains
  searchDomains: (query: string) => httpClient.get(`/api/domains/search?q=${encodeURIComponent(query)}`),
  getProjectDomains: (projectId: string) => httpClient.get(`/api/domains/project/${projectId}`),
  purchaseDomain: (data: { domain: string; projectId: number }) => httpClient.post('/api/domains/purchase', data),
  verifyDomainDns: (domainId: string) => httpClient.post(`/api/domains/${domainId}/verify-dns`),
  deleteDomain: (domainId: string) => httpClient.delete(`/api/domains/${domainId}`)
}

export default apiClient
